import { useNavigate } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import type { PriorityItem } from '../../utils/dashboard';
import { formatMinutes } from '../../utils/dashboard';
import DriverPill from '../DriverPill';
import PriorityChip from '../PriorityChip';
import WhyAffordance from '../ai/WhyAffordance';
import { WorkItemTypeBadge, workItemTypeHint } from '../workItems/WorkItemTypeTreatment';
import type { RationaleEntry } from '../../utils/aiRationale';
import { dashboardCopy } from '../../copy/dashboard';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

// ---------------------------------------------------------------------------
// PriorityList — three-at-most list of what matters today. Each row carries
// a driver pill so strategic context is visible without a drill-down, plus
// a "Why?" affordance showing the AI reasoning behind the ordering when
// available. When `onComplete` is wired, each row gains a tick-to-complete
// affordance so the user does not have to navigate to /work-items or hit
// Replan just to see a completion reflected.
// ---------------------------------------------------------------------------

interface PriorityListProps {
  items: PriorityItem[];
  loading?: boolean;
  rationaleMap?: Map<string, RationaleEntry>;
  onComplete?: (workItemId: string) => void;
}

export function PriorityList({ items, loading = false, rationaleMap, onComplete }: PriorityListProps) {
  const navigate = useNavigate();

  return (
    <div>
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {dashboardCopy.prioritiesHeader}
      </p>

      {loading ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-2xl bg-card/60" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No priorities set for today.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((item) => {
            const open = () => navigate(`/work-items?id=${item.workItem.id}`);
            const canComplete =
              onComplete && item.workItem.status !== 'Done' && item.workItem.status !== 'Cancelled';

            // Stretched-button pattern: the row itself is a plain div; an
            // absolute-positioned <button> beneath the content provides the
            // click-anywhere-to-open affordance. Inline interactives (Why,
            // Driver, Complete) sit above with pointer-events-auto so they
            // capture their own clicks without nesting buttons.
            return (
              <div
                key={item.workItem.id}
                className="group relative flex items-center gap-3 rounded-2xl border border-border bg-card/60 p-4 backdrop-blur transition-colors hover:border-primary focus-within:border-primary"
              >
                <button
                  type="button"
                  onClick={open}
                  className="absolute inset-0 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Open ${item.workItem.title}`}
                />

                {/* Scheduled time (tabular so columns align down the list). */}
                <div className="pointer-events-none relative min-w-[52px] text-[13px] font-medium tabular-nums text-muted-foreground">
                  {item.scheduledTime ?? '—'}
                </div>

                <div className="pointer-events-none relative min-w-0 flex-1">
                  <div className="flex items-center gap-1">
                    <WorkItemTypeBadge type={item.workItem.type} variant="compact" />
                    <p className="truncate text-base font-medium">{item.workItem.title}</p>
                    <span className="pointer-events-auto shrink-0">
                      <WhyAffordance
                        rationale={rationaleMap?.get(item.workItem.id)?.rationale}
                        secondary={rationaleMap?.get(item.workItem.id)?.strategyLink}
                        size="compact"
                        label="Why this priority"
                      />
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <span className="pointer-events-auto">
                      <DriverPill driver={item.workItem.strategicDriver} size="compact" />
                    </span>
                    {item.plannedMinutes !== undefined && (
                      <span className="text-xs text-muted-foreground">
                        {formatMinutes(item.plannedMinutes)}
                      </span>
                    )}
                    {(() => {
                      const hint = workItemTypeHint(item.workItem);
                      return hint ? (
                        <span className="text-xs text-muted-foreground">{hint}</span>
                      ) : null;
                    })()}
                  </div>
                </div>

                {canComplete && (
                  <div className="pointer-events-auto relative">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          onClick={() => onComplete!(item.workItem.id)}
                          className="shrink-0"
                          aria-label="Mark complete"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Mark complete</TooltipContent>
                    </Tooltip>
                  </div>
                )}

                <div className="pointer-events-none relative">
                  <PriorityChip priority={item.workItem.priority} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default PriorityList;
